// src/prototype/components/CompareTable.tsx
// Side-by-side comparison of 2–3 candidates: metric labels down the first column,
// one column per candidate, verdict-verb action button per column in the footer row.
import React from 'react';
import { X, Check } from 'lucide-react';
import { VerdictPill } from './VerdictPill';
import { VERDICTS } from '../theme/decisionTokens';
import { TOTAL_PORTFOLIO_SIZE, type DecisionBrief } from '../data/decisionBrief';

export interface CompareTableProps {
  briefs: DecisionBrief[];
  /** Opens that candidate's decision brief. */
  onFocus: (id: string) => void;
  onRemove: (id: string) => void;
  onProceed: (id: string) => void;
  /** Ids already in the Execute portfolio — their action button reads as done. */
  portfolioIds: string[];
}

const METRICS: { label: string; render: (b: DecisionBrief) => React.ReactNode }[] = [
  { label: 'Executive decision', render: (b) => <VerdictPill verdict={b.verdict} /> },
  { label: 'Annual ROI', render: (b) => <b className="text-[#18365E]">{b.roi}</b> },
  { label: 'Time to value', render: (b) => b.ttv },
  { label: 'Value score', render: (b) => <span className="font-semibold text-[#18365E]">{b.valueScore} / 100</span> },
  { label: 'Implementation score', render: (b) => <span className="font-semibold text-[#18365E]">{b.implScore} / 100</span> },
  { label: 'Portfolio rank', render: (b) => `#${b.rank} of ${TOTAL_PORTFOLIO_SIZE}` },
  {
    label: 'Top risk',
    render: (b) =>
      b.risks[0] ? (
        <span>
          {b.risks[0].name} <span className="text-[10px] text-[#717182]">({b.risks[0].severity})</span>
        </span>
      ) : (
        '—'
      ),
  },
  {
    label: 'First milestone',
    render: (b) =>
      b.roadmap[0] ? (
        <span>
          {b.roadmap[0].phase} · <span className="text-[#717182]">{b.roadmap[0].dur}</span>
        </span>
      ) : (
        '—'
      ),
  },
];

export const CompareTable: React.FC<CompareTableProps> = ({
  briefs,
  onFocus,
  onRemove,
  onProceed,
  portfolioIds,
}) => (
  <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-x-auto">
    <table className="w-full text-sm" style={{ minWidth: 560 }}>
      <thead>
        <tr>
          <th className="text-left text-[10px] font-semibold text-[#717182] uppercase tracking-wide px-4 py-3 w-44 align-bottom">
            Metric
          </th>
          {briefs.map((b) => (
            <th key={b.id} className="text-left px-4 py-3 align-bottom border-l border-[#e5e7eb]">
              <div className="flex items-start gap-2">
                <span className="w-6 h-6 rounded-full bg-[#18365E] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                  {b.rank}
                </span>
                <button
                  onClick={() => onFocus(b.id)}
                  className="text-xs font-bold text-[#18365E] hover:text-[#FF6E4C] text-left leading-snug transition-colors"
                >
                  {b.title}
                </button>
                <button
                  onClick={() => onRemove(b.id)}
                  aria-label={`Remove ${b.title} from comparison`}
                  title="Remove"
                  className="ml-auto text-[#717182] hover:text-[#FF6E4C] transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {METRICS.map((metric, i) => (
          <tr key={metric.label} className={i % 2 ? 'bg-[#F5F7FA]/60' : ''}>
            <td className="px-4 py-2.5 text-xs font-semibold text-[#717182]">{metric.label}</td>
            {briefs.map((b) => (
              <td key={b.id} className="px-4 py-2.5 text-xs text-[#4b5563] border-l border-[#e5e7eb]">
                {metric.render(b)}
              </td>
            ))}
          </tr>
        ))}
        <tr>
          <td className="px-4 py-3" />
          {briefs.map((b) => {
            const inPortfolio = portfolioIds.includes(b.id);
            return (
              <td key={b.id} className="px-4 py-3 border-l border-[#e5e7eb]">
                <button
                  onClick={() => onProceed(b.id)}
                  disabled={inPortfolio}
                  className="bg-[#FF6E4C] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 hover:bg-[#e85f3f] disabled:bg-emerald-600 disabled:hover:bg-emerald-600 transition-colors"
                >
                  <Check className="w-3 h-3" />
                  {inPortfolio ? 'In portfolio' : VERDICTS[b.verdict].verb}
                </button>
              </td>
            );
          })}
        </tr>
      </tbody>
    </table>
  </div>
);

export default CompareTable;
